/**
 * <lih-carousel> — shared mobile carousel behaviour for the Live In Hoops
 * landing page sections.
 *
 * Markup contract:
 *   <lih-carousel>
 *     <div data-lih-track>  ... slides are the direct children ...  </div>
 *     <div>
 *       <a data-lih-dot href="#slide-id" aria-current="true"></a>
 *       <a data-lih-dot href="#slide-id"></a>
 *     </div>
 *   </lih-carousel>
 *
 * State lives on aria-current, so each section styles its own active dot with
 * a scoped [aria-current='true'] selector — no shared class names.
 *
 * The dots are real anchors: without JS they still scroll to their slide.
 */
class LihCarousel extends HTMLElement {
  /* What counts as the visitor taking over from autoplay. */
  static INTERACTIONS = ['pointerdown', 'wheel', 'keydown'];

  connectedCallback() {
    this.track = this.querySelector('[data-lih-track]');
    this.dots = Array.from(this.querySelectorAll('[data-lih-dot]'));
    this.prev = this.querySelector('[data-lih-prev]');
    this.next = this.querySelector('[data-lih-next]');

    if (!this.track) return;
    if (this.dots.length < 2 && !this.prev && !this.next) return;

    this.slides = Array.from(this.track.children);
    this.activeIndex = 0;
    this.ticking = false;

    /* How many slides one step moves — arrows, drag-snap and autoplay all
       agree on this. 1 everywhere except the UGC reviews row, which shows
       more than one at once and has to land back on a whole page rather
       than split one across the viewport edge. */
    this.advanceBy = parseInt(this.dataset.lihStep, 10) || 1;

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    /* Above the breakpoint the track is a grid, not a scroller, and the dots
       are hidden. Watch for the layout flip so the state is re-evaluated when
       the viewport crosses it — a resize fires no scroll event. */
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.onScroll);
      this.observer.observe(this.track);
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollToSlide(index);
      });
    });

    /* Arrows step by whole slides from wherever the drag left off, rather than
       tracking a separate index that scrolling would desynchronise. */
    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));

    /* Open on a specific slide — used to put the featured product first on
       mobile without reordering the markup, which would move it on desktop
       too. Instant, so it is in place before the first paint settles. */
    const startAt = this.slides.findIndex((slide) => slide.hasAttribute('data-lih-start'));
    if (startAt > 0 && this.isScrollable()) this.scrollToSlide(startAt, 'auto');

    this.sync();

    const autoplay = parseInt(this.dataset.lihAutoplay || '0', 10);
    if (autoplay > 0) this.startAutoplay(autoplay);
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    this.stopAutoplay();
    clearTimeout(this.resumeTimer);
    for (const type of LihCarousel.INTERACTIONS) {
      this.removeEventListener(type, this.onInteraction);
    }
  }

  /**
   * @param {number} delay - Milliseconds between slides, and the quiet period
   *   after an interaction before advancing resumes.
   * @returns {void}
   */
  startAutoplay(delay) {
    /* Motion the visitor did not ask for is exactly what this setting covers. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.autoplayDelay = delay;
    this.runAutoplay();

    /* Interaction pauses rather than ends it. Resuming under someone's finger
       is the thing worth avoiding, not resuming at all — so the clock restarts
       once they have been still for one full interval. */
    for (const type of LihCarousel.INTERACTIONS) {
      this.addEventListener(type, this.onInteraction, { passive: true });
    }
  }

  /* Split from startAutoplay so resuming re-arms the interval without binding
     a second set of listeners. */
  runAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => {
      /* Laid out as a desktop grid there is nothing to advance. */
      if (!this.isScrollable()) return;
      this.scrollToSlide(this.atEnd ? 0 : this.activeIndex + this.advanceBy);
    }, this.autoplayDelay);
  }

  onInteraction = () => {
    this.stopAutoplay();
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => this.runAutoplay(), this.autoplayDelay);
  };

  stopAutoplay() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Deliberately measured from the scroll position rather than the slide index:
   * with more than one slide in view the last slide can never reach the left
   * edge, so the final index is never reported active.
   *
   * @returns {boolean} True once the track has run out of scroll.
   */
  get atEnd() {
    return this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 1;
  }

  /* True only while the track actually overflows, i.e. while it is a carousel.
     As a desktop grid the columns fit exactly, so scrollWidth === clientWidth. */
  isScrollable() {
    return this.track.scrollWidth - this.track.clientWidth > 1;
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.sync();
    });
  }

  /**
   * Nearest slide centre wins. A threshold-based IntersectionObserver stalls
   * mid-swipe when two slides both sit under the threshold; a centre distance
   * always resolves to exactly one slide, so the dot tracks the drag.
   *
   * Measured with rects rather than offsetLeft: the track is not a positioned
   * ancestor, so offsetLeft would resolve against whatever is.
   */
  sync() {
    const scrollable = this.isScrollable();

    /* A scrolling region has to be keyboard reachable (WCAG 2.1.1) — the
       slides themselves are not always focusable, so without this the content
       is unreachable without a pointer. As a desktop grid nothing scrolls, so
       the tab stop is removed rather than left as a dead one. */
    if (scrollable) {
      this.track.setAttribute('tabindex', '0');
    } else {
      this.track.removeAttribute('tabindex');
    }

    this.updateArrows();

    /* Laid out as a grid every slide sits under the track, and the centre
       nearest the midpoint is an inner one — which is how the second dot ended
       up active while the first slide was showing. */
    if (!scrollable) {
      this.setActive(0);
      return;
    }

    const bounds = this.track.getBoundingClientRect();
    const viewportCentre = bounds.left + bounds.width / 2;

    let nearest = 0;
    let shortest = Infinity;

    this.slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - viewportCentre);
      if (distance < shortest) {
        shortest = distance;
        nearest = index;
      }
    });

    this.setActive(nearest);
  }

  step(direction) {
    this.scrollToSlide(this.activeIndex + direction * this.advanceBy);
  }

  /* Arrows are disabled at the ends so they never look active while doing
     nothing. Kept in the DOM so the control positions do not shift. */
  updateArrows() {
    if (!this.prev && !this.next) return;

    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = this.atEnd;
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  scrollToSlide(index, behavior) {
    /* Clamped so an out of range index can never be requested. */
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[clamped];
    if (!slide) return;

    const padding = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    const delta =
      slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left - padding;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: delta, behavior: behavior || (reduced ? 'auto' : 'smooth') });
  }
}

if (!customElements.get('lih-carousel')) {
  customElements.define('lih-carousel', LihCarousel);
}
